import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  setPersistence,
  type Auth,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, type Firestore } from "firebase/firestore";

import { config } from "./config";

/** Where the "keep me signed in" choice lives. */
const REMEMBER_KEY = "shoku.contractor.remember";

/**
 * Contractors sign in from building sites, shared laptops and internet cafés as
 * often as from a personal machine, so the default is a session that dies with
 * the tab. `indexedDBLocalPersistence` is opt-in per sign-in, via the checkbox
 * on the login form.
 */
export function rememberMe(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) === "1";
  } catch {
    // Safari in Lockdown Mode and some privacy extensions throw on access
    // rather than returning null. The safe answer is the stricter one.
    return false;
  }
}

export function setRememberMe(value: boolean): void {
  try {
    localStorage.setItem(REMEMBER_KEY, value ? "1" : "0");
  } catch {
    /* Non-fatal: the session simply won't outlive the tab. */
  }
}

let app: FirebaseApp | undefined;
let authInstance: Auth | undefined;
let dbInstance: Firestore | undefined;

function ensureApp(): FirebaseApp {
  if (!app) app = initializeApp(config.firebase);
  return app;
}

export function auth(): Auth {
  if (!authInstance) {
    // `initializeAuth`, not `getAuth`, for one specific reason: getAuth wires up
    // the browser popup/redirect resolver, which lazily injects
    // https://apis.google.com/js/api.js to host its OAuth iframe. Shoku uses
    // email and password only — no OAuth provider, no popup, no redirect — so
    // that script is pure cost: it is third-party JS this app would have to
    // allow in `script-src`, weakening the strongest line in the CSP, and it
    // logs a policy violation on every session where the SDK decides to fetch
    // it (observed on mobile user agents, not desktop).
    //
    // Omitting popupRedirectResolver means signInWithPopup/Redirect would throw
    // if anyone ever calls them — which is the correct, loud failure for a
    // capability this app does not have.
    authInstance = initializeAuth(ensureApp(), {
      // Ordered by preference; the SDK falls through to the first that works.
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    });
    if (config.authEmulator) {
      connectAuthEmulator(authInstance, config.authEmulator, { disableWarnings: true });
    }
  }
  return authInstance;
}

export function db(): Firestore {
  if (!dbInstance) {
    dbInstance = getFirestore(ensureApp());
    if (config.firestoreEmulator) {
      connectFirestoreEmulator(
        dbInstance,
        config.firestoreEmulator.host,
        config.firestoreEmulator.port,
      );
    }
  }
  return dbInstance;
}

/**
 * Applies the persistence choice. Must be awaited BEFORE any sign-in call:
 * Firebase applies persistence at sign-in time, so setting it afterwards leaves
 * the just-created session on the previous setting.
 */
export async function applyPersistence(remember: boolean): Promise<void> {
  setRememberMe(remember);
  await setPersistence(auth(), remember ? indexedDBLocalPersistence : browserSessionPersistence);
}

/**
 * Returns a bearer token for the current user, or null when signed out.
 *
 * `forceRefresh` exists for one specific reason: POST /auth/complete is what
 * mints the `userType: "contractor"` custom claim, and the ID token already in
 * hand predates it. Without a forced refresh the very next claim-gated call
 * answers 403 and no amount of retrying with the same cached token recovers
 * (this is issue #74, which cost a day the first time).
 */
export async function idToken(forceRefresh = false): Promise<string | null> {
  const user = auth().currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Ends the session and clears every client-side cache.
 *
 * The cache wipe is not housekeeping — it is the whole point on a shared
 * machine. The mobile app clears its Hive boxes on sign-out for the same
 * reason; skipping it here would leave the next person at the keyboard looking
 * at the previous contractor's job list and payout history.
 */
export async function signOutCompletely(): Promise<void> {
  const { signOut } = await import("firebase/auth");
  await signOut(auth());
  try {
    sessionStorage.clear();
    const remembered = localStorage.getItem(REMEMBER_KEY);
    localStorage.clear();
    // The preference itself is not personal data and survives, so the checkbox
    // reflects the machine's habit rather than resetting on every sign-out.
    if (remembered !== null) localStorage.setItem(REMEMBER_KEY, remembered);
  } catch {
    /* Storage unavailable — nothing was cached to leak. */
  }
}
